import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  FieldNamesMarkedBoolean,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form/dist/types/form';

import { FormComponent, FormComponentProps } from '..';
import deepEqual from 'deep-equal';

export type WatchType = {
  value: any;
  parentName: string;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  trigger: UseFormTrigger<any>;
  touchedFields: FieldNamesMarkedBoolean<any>;
  isTouched: boolean;
  dirtyFields: FieldNamesMarkedBoolean<any>;
  isDirty: boolean;
};

export default function Watch(
  element: FormComponent,
  watchCallback: (watch: WatchType) => void,
  emptyCheck: boolean = true
): FormComponent {
  return ({ ...rest }) => (
    <WatchComponent
      element={element}
      watchCallback={watchCallback}
      emptyCheck={emptyCheck}
      {...rest}
    />
  );
}

export function WatchComponent({
  element,
  watchCallback,
  emptyCheck,
  ...rest
}: FormComponentProps & {
  element: FormComponent;
  watchCallback: (watch: WatchType) => void;
  emptyCheck: boolean;
}) {
  const Element = element;
  const [ChildComponent] = useState(() =>
    element ? (element({ ...rest }) as ReactElement) : null
  );
  const watchElement = useWatch({
    name: ChildComponent
      ? ChildComponent.props.parentName
        ? [ChildComponent.props.name]
            .flat()
            .map((name) => ChildComponent.props.parentName.concat('.', name))
        : ChildComponent.props.name
      : undefined,
  });
  const {
    setError,
    clearErrors,
    setValue,
    getValues,
    trigger,
    formState: { touchedFields, dirtyFields },
  } = useFormContext();
  const [lastValue, setLastValue] = useState(() => watchElement);

  const callback = useCallback(() => {
    let isTouched = false;
    let isDirty = false;

    // some components consist of multiple fields (therefore multiple names),
    // isTouched/isDirty represents state of the first field
    const n = ChildComponent?.props?.name;
    const childComponentName =
      typeof n === 'object' && Array.isArray(n) ? n[0] : n;
    if (touchedFields) {
      if (childComponentName && !ChildComponent?.props?.parentName) {
        isTouched = touchedFields[childComponentName] || false;
      } else {
        const path = ChildComponent.props.parentName.split('.');
        let fields = touchedFields;

        for (let i = 0; i < path.length && fields; i++) {
          fields = fields[path[i]];
        }

        isTouched = fields ? fields[childComponentName] : false;
      }
    }

    if (dirtyFields) {
      if (childComponentName && !ChildComponent?.props?.parentName) {
        isDirty = dirtyFields[childComponentName] || false;
      } else {
        const path = ChildComponent.props.parentName.split('.');
        let fields = dirtyFields;

        for (let i = 0; i < path.length && fields; i++) {
          fields = fields[path[i]];
        }

        isDirty = fields ? fields[childComponentName] : false;
      }
    }

    watchCallback({
      value: watchElement,
      parentName: ChildComponent.props.parentName,
      setError,
      clearErrors,
      setValue: (
        name,
        value,
        options = {
          shouldDirty: false,
          shouldValidate: false,
          shouldTouch: false,
        }
      ) => {
        if (
          !deepEqual(getValues(name), value) ||
          !deepEqual(options, {
            shouldDirty: false,
            shouldValidate: false,
            shouldTouch: false,
          })
        ) {
          setValue(name, value as any, options);
        }
      },
      getValues,
      trigger,
      isTouched: isTouched,
      touchedFields,
      isDirty,
      dirtyFields,
    });
  }, [
    watchCallback,
    watchElement,
    setError,
    clearErrors,
    getValues,
    setValue,
    trigger,
    touchedFields,
    dirtyFields,
    ChildComponent?.props?.name,
    ChildComponent?.props?.parentName,
  ]);

  useEffect(() => {
    if (deepEqual(lastValue, watchElement)) {
      return;
    }

    setLastValue(watchElement);

    if (emptyCheck) {
      if (watchElement) {
        callback();
      }
    } else {
      callback();
    }
  }, [callback, watchElement, emptyCheck, setLastValue, lastValue]);

  return Element ? <Element {...rest} /> : null;
}
